import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Drawer, Popconfirm, Select, Space, Spin, Table, Tag, Typography, message } from "antd";
import {
  apiErrorMessage,
  deliveryStatusColor,
  deliveryStatusLabel,
  DELIVERY_STATUS_OPTIONS,
  eventTypeLabel,
  formatDateTime,
  formatPayload,
} from "../labels";
import { FALLBACK_EVENT_TYPES, WebhookDeliveryAttempt, WebhookDeliveryDetail, WebhookDeliveryListItem, WebhookDeliveryStatus } from "../types";
import { useWebhookApi, webhookKeys } from "../useWebhookApi";

type Props = {
  subscriptionId: number;
};

const preStyle: React.CSSProperties = {
  margin: 0,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 280,
  overflow: "auto",
  background: "#f7f8fa",
  border: "1px solid #eef0f3",
  borderRadius: 8,
  padding: 12,
  fontSize: 12,
};

export const WebhookDeliveries: React.FC<Props> = ({ subscriptionId }) => {
  const api = useWebhookApi();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<WebhookDeliveryStatus | undefined>();
  const [eventType, setEventType] = useState<string | undefined>();
  const [openId, setOpenId] = useState<number | null>(null);

  const eventTypesQuery = useQuery({
    queryKey: webhookKeys.eventTypes,
    queryFn: async () => {
      const result = await api.listEventTypes();
      return Array.isArray(result) && result.length > 0 ? result : FALLBACK_EVENT_TYPES;
    },
  });

  const deliveriesQuery = useQuery({
    queryKey: [...webhookKeys.deliveries(subscriptionId), page, pageSize, status ?? "", eventType ?? ""],
    queryFn: async () => {
      const result = await api.listDeliveries(subscriptionId, { page, pageSize, status, eventType });
      return {
        data: Array.isArray(result?.data) ? result.data : [],
        total: result?.total ?? 0,
      };
    },
    keepPreviousData: true,
  });

  const detailQuery = useQuery({
    queryKey: webhookKeys.delivery(openId ?? 0),
    queryFn: () => api.getDelivery(openId as number),
    enabled: openId != null,
  });

  const redeliverMutation = useMutation({
    mutationFn: (id: number) => api.redeliver(id),
    onSuccess: (detail) => {
      queryClient.setQueryData(webhookKeys.delivery(detail.id), detail);
      queryClient.invalidateQueries({ queryKey: webhookKeys.deliveries(subscriptionId) });
      queryClient.invalidateQueries({ queryKey: webhookKeys.subscription(subscriptionId) });
      queryClient.invalidateQueries({ queryKey: webhookKeys.list });
      message.success("Leveransen köas igen. Samma händelse-id behålls.");
    },
    onError: (error) => {
      message.error(apiErrorMessage(error, "Kunde inte köa om leveransen"));
    },
  });

  const eventTypeOptions = [
    { value: "webhook.test", label: eventTypeLabel("webhook.test") },
    ...(eventTypesQuery.data ?? FALLBACK_EVENT_TYPES).map((type) => ({
      value: type,
      label: `${eventTypeLabel(type)} (${type})`,
    })),
  ];

  const detail = detailQuery.data;

  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 180 }}
          options={DELIVERY_STATUS_OPTIONS}
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        />
        <Select
          allowClear
          placeholder="Händelse"
          style={{ width: 280 }}
          options={eventTypeOptions}
          value={eventType}
          onChange={(value) => {
            setEventType(value);
            setPage(1);
          }}
        />
        <Button onClick={() => deliveriesQuery.refetch()}>Uppdatera</Button>
      </Space>

      <Table<WebhookDeliveryListItem>
        rowKey="id"
        dataSource={deliveriesQuery.data?.data ?? []}
        loading={deliveriesQuery.isLoading}
        scroll={{ x: 980 }}
        locale={{ emptyText: deliveriesQuery.isError ? "Kunde inte hämta leveranser" : "Inga leveranser" }}
        pagination={{
          current: page,
          pageSize,
          total: deliveriesQuery.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          onChange: (nextPage, nextSize) => {
            setPage(nextSize !== pageSize ? 1 : nextPage);
            setPageSize(nextSize);
          },
        }}
      >
        <Table.Column<WebhookDeliveryListItem>
          title="Skapad"
          dataIndex="createdAt"
          width={150}
          render={(value: string) => formatDateTime(value)}
        />
        <Table.Column<WebhookDeliveryListItem>
          title="Händelse"
          dataIndex="eventType"
          render={(value: string) => (
            <span>
              {eventTypeLabel(value)} <Typography.Text type="secondary">{value}</Typography.Text>
            </span>
          )}
        />
        <Table.Column<WebhookDeliveryListItem>
          title="Status"
          dataIndex="status"
          width={130}
          render={(value: string) => <Tag color={deliveryStatusColor(value)}>{deliveryStatusLabel(value)}</Tag>}
        />
        <Table.Column<WebhookDeliveryListItem> title="Försök" dataIndex="attemptCount" width={80} />
        <Table.Column<WebhookDeliveryListItem>
          title="HTTP"
          dataIndex="lastStatusCode"
          width={80}
          render={(value: number | null) => value ?? "–"}
        />
        <Table.Column<WebhookDeliveryListItem>
          title="Senaste försök"
          dataIndex="lastAttemptAt"
          width={150}
          render={(value: string | null) => formatDateTime(value)}
        />
        <Table.Column<WebhookDeliveryListItem>
          title="Fel"
          dataIndex="lastError"
          ellipsis
          render={(value: string | null) => value || "–"}
        />
        <Table.Column<WebhookDeliveryListItem>
          title=""
          width={90}
          render={(_, record) => (
            <Button size="small" onClick={() => setOpenId(record.id)}>
              Visa
            </Button>
          )}
        />
      </Table>

      <Drawer
        title={detail ? `${eventTypeLabel(detail.eventType)} #${detail.id}` : "Leverans"}
        open={openId != null}
        onClose={() => setOpenId(null)}
        width={720}
        extra={
          openId != null && (
            <Popconfirm
              title="Köa leveransen igen?"
              description="Samma händelse-id används, så mottagaren kan känna igen en dubblett. Utskick sker inom en minut."
              okText="Skicka igen"
              cancelText="Avbryt"
              onConfirm={() => redeliverMutation.mutate(openId)}
            >
              <Button type="primary" loading={redeliverMutation.isLoading}>
                Skicka igen
              </Button>
            </Popconfirm>
          )
        }
      >
        {detailQuery.isLoading && <Spin />}
        {detailQuery.isError && <Alert type="error" showIcon message="Kunde inte hämta leveransen" />}
        {detail && <DeliveryDetailBody detail={detail} />}
      </Drawer>
    </>
  );
};

const DeliveryDetailBody: React.FC<{ detail: WebhookDeliveryDetail }> = ({ detail }) => {
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Tag color={deliveryStatusColor(detail.status)}>{deliveryStatusLabel(detail.status)}</Tag>
        <Typography.Text type="secondary">{detail.eventType}</Typography.Text>
      </Space>
      {detail.lastError && <Alert type="error" showIcon message={detail.lastError} />}
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        <Typography.Text type="secondary">Händelse-id </Typography.Text>
        <Typography.Text code copyable={{ tooltips: ["Kopiera", "Kopierad"] }}>
          {detail.eventId}
        </Typography.Text>
      </Typography.Paragraph>
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        Produkt-id: {detail.entityId ?? "–"} · Försök: {detail.attemptCount} · Nästa försök: {formatDateTime(detail.nextAttemptAt)}
      </Typography.Paragraph>
      <div>
        <Typography.Text strong>Payload</Typography.Text>
        <pre style={{ ...preStyle, marginTop: 8 }}>{formatPayload(detail.payload)}</pre>
      </div>
      <div>
        <Typography.Text strong>Försök</Typography.Text>
        <Table<WebhookDeliveryDetail["attempts"][number]>
          style={{ marginTop: 8 }}
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={detail.attempts ?? []}
          locale={{ emptyText: "Inga försök ännu" }}
        >
          <Table.Column title="#" dataIndex="attemptNumber" width={50} />
          <Table.Column title="Tid" dataIndex="attemptedAt" render={(value: string) => formatDateTime(value)} />
          <Table.Column title="HTTP" dataIndex="statusCode" width={70} render={(value: number | null) => value ?? "–"} />
          <Table.Column title="ms" dataIndex="durationMs" width={70} />
          <Table.Column<WebhookDeliveryAttempt>
            title="Svar"
            render={(_, attempt) => (
              <Space direction="vertical" size={4}>
                {attempt.error && <Typography.Text type="danger">{attempt.error}</Typography.Text>}
                {attempt.responseBody && <pre style={{ ...preStyle, maxHeight: 120 }}>{attempt.responseBody}</pre>}
                {!attempt.error && !attempt.responseBody && "–"}
              </Space>
            )}
          />
        </Table>
      </div>
    </Space>
  );
};
